import { ROUTE_ID_PLACEHOLDER, sanitizeUrlProperties, toRoutePattern } from "./route-pattern.js";

export interface SentryRequestLike {
	url?: string;
	query_string?: unknown;
	headers?: Record<string, string>;
}

export interface SentryBreadcrumbLike {
	message?: string;
	data?: Record<string, unknown>;
}

export interface SentryExceptionValueLike {
	value?: string;
}

export interface SentryEventLike {
	message?: string;
	request?: SentryRequestLike;
	breadcrumbs?: SentryBreadcrumbLike[];
	exception?: { values?: SentryExceptionValueLike[] };
	extra?: Record<string, unknown>;
	contexts?: Record<string, unknown>;
}

const URL_IN_TEXT_PATTERN = /\bhttps?:\/\/\S+/g;
const EMAIL_IN_TEXT_PATTERN = /[A-Za-z0-9._%+-]+@[A-Za-z0-9-]+\.[A-Za-z0-9.-]+/g;
const UUID_IN_TEXT_PATTERN = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;
const REDACTED_TEXT = "[redacted]";
const REFERER_HEADER_NAMES = ["referer", "referrer"];
const NAVIGATION_URL_KEYS = ["from", "to"];
const MAX_SCRUB_DEPTH = 8;

export const redactText = (text: string): string =>
	text
		.replace(URL_IN_TEXT_PATTERN, toRoutePattern)
		.replace(EMAIL_IN_TEXT_PATTERN, REDACTED_TEXT)
		.replace(UUID_IN_TEXT_PATTERN, ROUTE_ID_PLACEHOLDER);

const redactNested = (value: unknown, ancestors: Set<object>, depth: number): unknown => {
	if (typeof value === "string") {
		return redactText(value);
	}
	if (typeof value !== "object" || value === null || value instanceof Date) {
		return value;
	}
	if (depth >= MAX_SCRUB_DEPTH || ancestors.has(value)) {
		return REDACTED_TEXT;
	}
	ancestors.add(value);
	const redacted = Array.isArray(value)
		? value.map((item) => redactNested(item, ancestors, depth + 1))
		: Object.fromEntries(Object.entries(value).map(([key, entry]) => [key, redactNested(entry, ancestors, depth + 1)]));
	ancestors.delete(value);
	return redacted;
};

const redactRecord = (record: Record<string, unknown>): Record<string, unknown> => {
	const ancestors = new Set<object>([record]);
	return Object.fromEntries(Object.entries(record).map(([key, value]) => [key, redactNested(value, ancestors, 1)]));
};

export const sanitizeSentryBreadcrumb = (breadcrumb: SentryBreadcrumbLike): void => {
	if (typeof breadcrumb.message === "string") {
		breadcrumb.message = redactText(breadcrumb.message);
	}
	if (breadcrumb.data) {
		breadcrumb.data = sanitizeUrlProperties(breadcrumb.data, NAVIGATION_URL_KEYS);
	}
};

const sanitizeSentryRequest = (request: SentryRequestLike): void => {
	if (typeof request.url === "string") {
		request.url = toRoutePattern(request.url);
	}
	request.query_string = undefined;
	if (!request.headers) {
		return;
	}
	const headers: Record<string, string> = {};
	for (const [key, value] of Object.entries(request.headers)) {
		headers[key] = REFERER_HEADER_NAMES.includes(key.toLowerCase()) ? toRoutePattern(value) : value;
	}
	request.headers = headers;
};

export const sanitizeSentryEvent = (event: SentryEventLike): void => {
	if (typeof event.message === "string") {
		event.message = redactText(event.message);
	}
	if (event.request) {
		sanitizeSentryRequest(event.request);
	}
	for (const exceptionValue of event.exception?.values ?? []) {
		if (typeof exceptionValue.value === "string") {
			exceptionValue.value = redactText(exceptionValue.value);
		}
	}
	for (const breadcrumb of event.breadcrumbs ?? []) {
		sanitizeSentryBreadcrumb(breadcrumb);
	}
	if (event.extra) {
		event.extra = redactRecord(event.extra);
	}
	if (event.contexts) {
		event.contexts = redactRecord(event.contexts);
	}
};
